import { useCallback } from 'react';

import { useAuth } from '@/app/providers/auth/authContext';
import { progressionFor } from '@/core/engine/orchestrator';
import { uidSchema, type Match, type Team, type Tournament, type Uid } from '@/core/schemas';
import {
  applyProgression,
  startTournament,
  advancePhase,
  resetTournament,
  setRegistrationOpen,
  setTournamentPaused,
  type ProgressionOutcome,
} from '@/infrastructure/services/tournamentService';

/** What a reconcile run did: bracket slots first, then the phase transition. */
export interface ReconcileOutcome {
  readonly progression: ProgressionOutcome;
  readonly nextPhase: { phaseId: string; matchCount: number } | null;
}

/**
 * The tournament director's write actions, bound to the signed-in admin.
 *
 * After any result change the two follow-ups — filling the next bracket slots
 * and, if a phase just finished, planning the next one — are triggered here.
 * This is the client-side stand-in for the Cloud Function noted in
 * docs/SECURITY.md §6.2; the engine that computes them stays pure.
 */
export function useAdminActions() {
  const { session } = useAuth();
  const actorUid: Uid | null =
    session.status === 'authenticated' ? uidSchema.parse(session.account.uid) : null;

  const requireUid = useCallback((): Uid => {
    if (actorUid === null) {
      throw new Error('Nicht angemeldet');
    }
    return actorUid;
  }, [actorUid]);

  const start = useCallback(
    (tournament: Tournament, teams: readonly Team[]) =>
      startTournament({ tournament, teams, actorUid: requireUid() }),
    [requireUid],
  );

  const pause = useCallback(
    (tournament: Tournament) =>
      setTournamentPaused({ tournament, paused: true, actorUid: requireUid() }),
    [requireUid],
  );

  const resume = useCallback(
    (tournament: Tournament) =>
      setTournamentPaused({ tournament, paused: false, actorUid: requireUid() }),
    [requireUid],
  );

  const openRegistration = useCallback(
    (tournament: Tournament) =>
      setRegistrationOpen({ tournament, isOpen: true, actorUid: requireUid() }),
    [requireUid],
  );

  const closeRegistration = useCallback(
    (tournament: Tournament) =>
      setRegistrationOpen({ tournament, isOpen: false, actorUid: requireUid() }),
    [requireUid],
  );

  const reset = useCallback(
    (tournament: Tournament, matches: readonly Match[]) =>
      resetTournament({ tournament, matches, actorUid: requireUid() }),
    [requireUid],
  );

  /**
   * Reconciles the bracket after results changed: advance progression first,
   * then plan the next phase if the current one is complete. Safe to call after
   * every change — both steps are idempotent.
   *
   * Whatever the progression refused to overwrite is handed back rather than
   * swallowed: a corrected result that would change an already played follow-up
   * match is the director's call, not the engine's.
   */
  const reconcile = useCallback(
    async (
      tournament: Tournament,
      teams: readonly Team[],
      matches: readonly Match[],
    ): Promise<ReconcileOutcome> => {
      const uid = requireUid();
      let progression: ProgressionOutcome = { applied: 0, blocked: [] };

      if (tournament.currentPhaseId !== null) {
        const mutations = progressionFor(tournament, tournament.currentPhaseId, matches);
        progression = await applyProgression({ tournament, matches, mutations, actorUid: uid });
      }

      const nextPhase = await advancePhase({ tournament, teams, matches, actorUid: uid });

      return { progression, nextPhase };
    },
    [requireUid],
  );

  return {
    start,
    pause,
    resume,
    reset,
    reconcile,
    openRegistration,
    closeRegistration,
    isReady: actorUid !== null,
  };
}
